/**
 * Onboarding Tour Configuration
 *
 * Defines the guided coachmark tour shown to new users. Each step points at a
 * real element in the app via a `[data-tour="…"]` anchor and renders a tooltip
 * card next to it. The narrative mirrors the core loop:
 *   plan a meal → build recipes → generate a shopping list → invite your group.
 *
 * Anchor names match the design mockup (docs/Meal Mate (standalone).html):
 *   nav, qa-plan, qa-recipes, qa-shopping, insights, group.
 */

export type TourPlacement = 'top' | 'bottom' | 'left' | 'right' | 'center'

export interface TourStep {
  /** Stable id, also used as the value of the `data-tour` anchor (except centered steps). */
  id: string
  /** CSS selector of the element to highlight. Omit for centered (modal) steps. */
  target?: string
  /** Route to ensure we are on before showing this step. */
  route?: string
  /** Preferred tooltip placement relative to the target. */
  placement: TourPlacement
  title: string
  body: string
  /** Optional eyebrow label shown above the title (e.g. step number in the loop). */
  eyebrow?: string
}

/**
 * The tour lives entirely on the Dashboard (`/home`), where every entry point of
 * the core loop is reachable — so we never have to drive the user across routes
 * mid-tour, which keeps the experience robust.
 */
export const ONBOARDING_TOUR: TourStep[] = [
  {
    id: 'welcome',
    placement: 'center',
    route: '/home',
    title: 'Welcome to Meal Mate',
    body: "Let's take a quick tour of how to plan your week — from recipe to shopping list. It takes about 30 seconds.",
  },
  {
    id: 'nav',
    target: '[data-tour="nav"]',
    route: '/home',
    placement: 'bottom',
    title: 'Your kitchen, in four places',
    body: 'Dashboard, Meals, Recipes and Shopping Lists. Everything you need lives behind these four links.',
  },
  {
    id: 'qa-plan',
    target: '[data-tour="qa-plan"]',
    route: '/home',
    placement: 'bottom',
    eyebrow: 'Step 1',
    title: 'Plan a meal',
    body: 'Pick a recipe and drop it onto your calendar. This is the heart of Meal Mate — schedule what you’ll cook and when.',
  },
  {
    id: 'qa-recipes',
    target: '[data-tour="qa-recipes"]',
    route: '/home',
    placement: 'bottom',
    eyebrow: 'Step 2',
    title: 'Build your recipe collection',
    body: 'Add or browse recipes here. The meals in your collection are what you plan onto the calendar.',
  },
  {
    id: 'qa-shopping',
    target: '[data-tour="qa-shopping"]',
    route: '/home',
    placement: 'bottom',
    eyebrow: 'Step 3',
    title: 'Generate a shopping list',
    body: 'Auto-build a shopping list from your planned meals — ingredients are pulled in and merged for you.',
  },
  {
    id: 'insights',
    target: '[data-tour="insights"]',
    route: '/home',
    placement: 'top',
    title: 'Kitchen Insights',
    body: 'Once you start cooking and shopping, this fills up with your most-cooked recipes and most-bought items.',
  },
  {
    id: 'group',
    target: '[data-tour="group"]',
    route: '/home',
    placement: 'top',
    eyebrow: 'Step 4',
    title: 'Plan together',
    body: 'Invite your household to share recipes, meal plans and shopping lists. Meal Mate is better as a group.',
  },
  {
    id: 'finish',
    placement: 'center',
    title: "You're all set!",
    body: 'Start by planning your first meal. You can replay this tour any time from your profile.',
  },
]

/** localStorage key recording that a user finished or dismissed the tour. */
export const ONBOARDING_DONE_KEY = 'mm_onb_tour_done'

/**
 * Getting-started checklist.
 *
 * Coexists with the tour: each task auto-completes when the user actually does
 * it (detected from real data), can be manually ticked/unticked, and has a
 * "Show me" action that jumps the guided tour to the matching step (`tourStepId`).
 */
export interface ChecklistTask {
  /** Stable id — also the key for auto-detection and manual overrides. */
  id: 'recipe' | 'meal' | 'shopping' | 'group'
  label: string
  /** Short one-liner used in the compact floating checklist. */
  description: string
  /** Richer explanation used on the Help page. */
  details: string
  /** Tour step this task maps to (for the "Show me" button). */
  tourStepId: string
  /** Where the task's primary action takes the user. */
  route: string
  /** Label for the primary action button on the Help page. */
  cta: string
}

export const CHECKLIST_TASKS: ChecklistTask[] = [
  {
    id: 'recipe',
    label: 'Add a recipe',
    description: 'Start your collection — the meals you can plan.',
    details:
      'Your recipe collection is the foundation of everything. Create recipes with a rich-text editor for the instructions and a searchable ingredient builder, browse them by category, and open a recipe’s detail page. Every recipe can then be planned onto your calendar and feeds shopping-list generation.',
    tourStepId: 'qa-recipes',
    route: '/recipes',
    cta: 'Go to Recipes',
  },
  {
    id: 'meal',
    label: 'Plan a meal',
    description: 'Drop a recipe onto your calendar.',
    details:
      'Plan what you’ll cook and when. Pick a recipe, choose one or several days (multi-day planning), and set a meal type — breakfast, lunch, dinner — and an optional time. Switch between a list view and a drag-and-drop calendar to reschedule meals at any time.',
    tourStepId: 'qa-plan',
    route: '/meals',
    cta: 'Plan a meal',
  },
  {
    id: 'shopping',
    label: 'Generate a shopping list',
    description: 'Auto-build it from your planned meals.',
    details:
      'Turn your plan into a shopping list automatically. The Sync Meals wizard gathers and merges all the ingredients from the meals in a date range, so you never miss an item. Check things off as you shop, reorder them by drag-and-drop, and add extras manually.',
    tourStepId: 'qa-shopping',
    route: '/shopping-lists',
    cta: 'Go to Shopping Lists',
  },
  {
    id: 'group',
    label: 'Invite your group',
    description: 'Share recipes and lists with your household.',
    details:
      'Meal Mate is better together. Create or join a household group and invite members to share recipes, meal plans and shopping lists. Manage roles, and keep up with invitations and notifications as your group collaborates.',
    tourStepId: 'group',
    route: '/user?tab=groups',
    cta: 'Manage your group',
  },
]

/** Feature-area reference shown on the Help page ("Explore the app"). */
export interface HelpFeature {
  id: string
  /** SVG path `d` data for a monocolor stroke icon (viewBox 0 0 24 24). */
  icon: string
  title: string
  summary: string
  /** Notable capabilities, one line each. */
  points: string[]
  route: string
  linkLabel: string
}

export const HELP_FEATURES: HelpFeature[] = [
  {
    id: 'dashboard',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    title: 'Dashboard',
    summary: 'Your kitchen at a glance — what’s planned and how things are going.',
    points: [
      'Today’s menu and an “Upcoming meals” list',
      'A 7-day “This Week” strip with per-day meal counts',
      'Stat cards: recipes, meals planned, active lists, group members',
      'Kitchen Insights: most-cooked recipes and most-bought items, with a 30d / 90d / 1-year toggle',
    ],
    route: '/home',
    linkLabel: 'Open Dashboard',
  },
  {
    id: 'meals',
    icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
    title: 'Meals',
    summary: 'The planner — schedule what you’ll cook and when.',
    points: [
      'List view and a drag-and-drop calendar view',
      'Create-meal wizard: pick a recipe, choose one or several days, set a meal type and time',
      'Recurrence: cook once vs. cook fresh each day',
      'Filter and reschedule meals at any time',
    ],
    route: '/meals',
    linkLabel: 'Open Meals',
  },
  {
    id: 'recipes',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    title: 'Recipes',
    summary: 'Your collection of meals to cook.',
    points: [
      'Search by name or ingredient and filter by category',
      'Rich-text editor for instructions plus a reusable ingredient builder',
      'Recipe detail pages with ingredients and steps',
      '“Plan Meal” links a recipe straight into your calendar',
    ],
    route: '/recipes',
    linkLabel: 'Open Recipes',
  },
  {
    id: 'shopping',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6M9 16h6',
    title: 'Shopping Lists',
    summary: 'Auto-generated from your plan, or built by hand.',
    points: [
      'Sync Meals wizard: gather and merge ingredients from a date range',
      'Auto-generated vs. manually added items, grouped by category',
      'Check items off, reorder by drag-and-drop, edit quantities',
      'Merge duplicate ingredients when meals overlap',
    ],
    route: '/shopping-lists',
    linkLabel: 'Open Shopping Lists',
  },
  {
    id: 'groups',
    icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
    title: 'Groups & sharing',
    summary: 'Plan together with your household.',
    points: [
      'Create or join groups and invite members',
      'Roles: owner, admin, member',
      'Invitations and notifications keep everyone in sync',
      'Shared recipes, meal plans and shopping lists',
    ],
    route: '/user?tab=groups',
    linkLabel: 'Manage Groups',
  },
  {
    id: 'account',
    icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    title: 'Account & security',
    summary: 'Manage your profile and keep your account safe.',
    points: [
      'Edit your profile information',
      'Two-Factor Authentication setup and management',
      'Change your password securely',
      'Review your notifications',
    ],
    route: '/user',
    linkLabel: 'Open Profile',
  },
]

/** localStorage keys for the checklist (per user). */
export const CHECKLIST_OVERRIDES_KEY = 'mm_onb_tasks'
export const CHECKLIST_DISMISSED_KEY = 'mm_onb_checklist_dismissed'
